import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled || open ? "border-b border-white/10 bg-[#0F0F23]/90 backdrop-blur-xl" : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
                    <img src="/assets/logo.png" alt="Rongeen TV" className="h-10 w-auto rounded-md" />
                    <span className="hidden flex-col leading-tight sm:flex">
                        <span className="font-display text-sm font-black uppercase tracking-[0.2em] text-white">Rongeen TV</span>
                        <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-[#FFA200]">Engage · Educate · Entertain</span>
                    </span>
                </Link>
                <nav className="hidden items-center gap-7 xl:flex" data-testid="nav-desktop">
                    {NAV_LINKS.slice(0, 5).map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={link.testId}
                            className={({ isActive }) =>
                                `relative text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                                    isActive ? "text-[#FFA200]" : "text-white/75 hover:text-white"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-contact-cta"
                        className="rounded-full bg-[#C8102E] px-5 py-2.5 text-[13px] font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:bg-[#8F0A20] hover:shadow-lg hover:shadow-[#C8102E]/30"
                    >
                        Contact Us
                    </Link>
                </nav>
                <button
                    type="button"
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                    onClick={() => setOpen((v) => !v)}
                    className="rounded-full border border-white/15 p-2.5 text-white xl:hidden"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-t border-white/10 bg-[#0F0F23]/95 backdrop-blur-xl xl:hidden"
                    >
                        <div className="flex flex-col gap-1 px-4 py-4">
                            {NAV_LINKS.map((link) => (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    data-testid={`mobile-${link.testId}`}
                                    className={({ isActive }) =>
                                        `rounded-lg px-4 py-3 text-sm font-bold uppercase tracking-[0.15em] transition-colors ${
                                            isActive ? "bg-[#C8102E]/15 text-[#FFA200]" : "text-white/80 hover:bg-white/5"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
